// JobSummary.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Chip, Paper, Link, Typography, Stack, Box, Grid } from '@mui/material';
import { CheckCircle, HourglassEmpty, Cancel, PlayCircleFilled } from "@mui/icons-material";

interface SubStatus {
  statusId: number;
  statusName: string;
  jobCount: number;
  Status_id: number
}

interface JobSummaryProps {
  summaryCountData: {
    totalCount?: number;
    jobTypeCounts?: Record<string, number>;
    jobStatusCounts?: Record<string, SubStatus[]>;
  } | null;
}

const JobSummary: React.FC<JobSummaryProps> = ({ summaryCountData }) => {
  const navigate = useNavigate();

  const handleSubstatusClick = (statusId: number) => {
    const basePath = window.location.pathname.split('/')[1];
    navigate(`/${basePath}/jobs/joblisting?statusId=${statusId}`);
  };

  if (!summaryCountData || !summaryCountData.jobTypeCounts || !summaryCountData.jobStatusCounts) {
    return <Typography variant="h6" style={{ fontSize: '11px' }}>Loading job summary...</Typography>;
  }

  const { totalCount, jobTypeCounts, jobStatusCounts } = summaryCountData;
  const { AR = 0, SSD = 0, MSD = 0 } = jobTypeCounts;

  // Status card data
  const statusCards = [
    { 
      title: "InProgress", 
      icon: <HourglassEmpty fontSize="large" />,
      color: "#DBEAFE",
      textColor: "#2563EB",
      count: jobStatusCounts?.InProgress?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.InProgress || []
    },
    { 
      title: "Success", 
      icon: <PlayCircleFilled fontSize="large" />,
      color: "#E8F5E9",
      textColor: "#388E3C",
      count: jobStatusCounts?.Success?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.Success || []
    },
    { 
      title: "Dispatch", 
      icon: <Cancel fontSize="large" />,
      color: "#E5E7EB",
      textColor: "#111827",
      count: jobStatusCounts?.Dispatch?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.Dispatch || []
    },
    { 
      title: "Dispatch Failed", 
      icon: <CheckCircle fontSize="large" />,
      color: "#FDE8E8",
      textColor: "#D32F2F",
      count: jobStatusCounts?.Dispatch_Failed?.reduce((sum, s) => sum + s.jobCount, 0) || 0,
      substatuses: jobStatusCounts?.Dispatch_Failed || []
    }
  ];

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
        <Paper
            sx={{
            padding: '16px',
            width:"100%",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e0e0e0",
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Heading */}
            <h2 style={{ fontWeight: "bold", fontSize: '15px' }}>Jobs Summary</h2>

            {/* Job Count Boxes */}
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {/* No of Jobs */}
                <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: "#555", fontSize: '11px' }}>No of Jobs</Typography>
                <Typography sx={{ fontWeight: "bold", color: "#222", fontSize: '11px' }}>
                    {totalCount ?? 0}
                </Typography>
                </Box>

                {/* AR */}
                <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: "#555", fontSize: '11px' }}>No of AR Jobs</Typography>
                <Typography sx={{ fontWeight: "bold", color: "#222", fontSize: '11px' }}>
                    {AR ?? 0}
                </Typography>
                </Box>

                {/* SSD */}
                <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: "#555", fontSize: '11px' }}>No of SSD Jobs</Typography>
                <Typography sx={{ fontWeight: "bold", color: "#222", fontSize: '11px' }}>
                    {SSD ?? 0}
                </Typography>
                </Box>

                {/* MSD */}
                <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: "#555", fontSize: '11px' }}>No of MSD Jobs</Typography>
                <Typography sx={{ fontWeight: "bold", color: "#222", fontSize: '11px' }}>
                    {MSD ?? 0}
                </Typography>
                </Box>
            </Box>
            </Box>
        </Paper>

      {/* Status Cards - Responsive Grid */}
      <Grid container spacing={1}>
        {statusCards.map((card, index) => (
          <Grid item xs={16} sm={10} md={2.95} key={index}>
            <Paper
              sx={{
                padding: '16px',
                borderRadius: "12px",
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: "#ffffff",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {/* <Box sx={{ bgcolor: "#dfd9d98c", padding: 1, borderRadius: 2, display: "inline-block" }}>
                  {card.icon}
                </Box> */}
                <Badge
                  sx={{
                    backgroundColor: card.color,
                    color: card.textColor,
                    padding: "6px 20px",
                    borderRadius: "25px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    height: '32px'
                  }}
                >
                  {card.title+":"}&nbsp;&nbsp;{card.count}
                </Badge>
              </Box>
              
              
              
              {/* Substatuses list */}
               <Box sx={{ mt: 2, maxHeight: '120px', overflowY: 'auto' }}>
                {card.substatuses
                  .filter(substatus => substatus.jobCount > 0)
                  .map((substatus, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 1 
                      }}
                    >
                      <Link 
                        onClick={() => handleSubstatusClick(substatus?.statusId)} 
                        sx={{ 
                          cursor: 'pointer', 
                          fontSize: '11px',
                          color: card.textColor,
                          textDecorationColor: card.textColor,
                          '&:hover': { 
                            textDecoration: 'underline',
                            textDecorationColor: card.textColor
                          }
                        }}
                      >
                        {substatus.statusName}
                      </Link>
                      <Chip 
                        label={substatus.jobCount} 
                        size="small"
                        sx={{ 
                          backgroundColor: card.color, 
                          color: card.textColor,
                          minWidth: "30px",
                          fontSize: '11px',
                          height: '22px'
                        }} 
                      />
                    </Box>
                  ))
                }
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default JobSummary;